import type { PaginatedResponse } from '@/types/credits';
import type {
  NotificationPreferences,
  ToggleUserActiveData,
  UpdateUserData,
  User,
  UserDetails,
  UsersQuery,
  UserWithoutOrganization,
} from '@/types/user';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Users Service
 * Handles all user-related API endpoints from /api/v1/users/*
 */

export const usersService = {
  /**
   * Get user by ID (with full details)
   */
  async getUser(userId: string): Promise<User> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<User>(`/api/v1/users/${userId}`, {}, token);
  },

  /**
   * Get user details (for profile pages)
   */
  async getUserDetails(userId: string, organizationId?: string): Promise<UserDetails> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const query = organizationId
      ? `?organization_id=${encodeURIComponent(organizationId)}`
      : '';

    return authenticatedRequest<UserDetails>(`/api/v1/users/${userId}${query}`, {}, token);
  },

  /**
   * Get users with pagination and filters
   */
  async getUsers(query?: UsersQuery): Promise<PaginatedResponse<User>> {
    const searchParams = new URLSearchParams();

    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);
    if (query?.name) searchParams.append('name', query.name);
    if (query?.email) searchParams.append('email', query.email);
    if (query?.status) searchParams.append('status', query.status);
    if (query?.organizationId) searchParams.append('organizationId', query.organizationId);

    // Handle roleNames array
    if (query?.roleNames && query.roleNames.length > 0) {
      query.roleNames.forEach(roleName => {
        searchParams.append('roleNames', roleName);
      });
    }

    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<PaginatedResponse<User>>(
      `/api/v1/users?${searchParams.toString()}`,
      {},
      token
    );
  },

  /**
   * Update an employee/user
   */
  async updateUser(userId: string, data: UpdateUserData): Promise<User> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<User>(
      `/api/v1/users/${userId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Toggle user active status
   */
  async toggleUserActive(userId: string, data: ToggleUserActiveData): Promise<User> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<User>(
      `/api/v1/users/${userId}/toggle-active`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(
      `/api/v1/users/${userId}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  /**
   * Get all users without an organization (SUPER_ADMIN only)
   */
  async getUsersWithoutOrganization(): Promise<UserWithoutOrganization[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<UserWithoutOrganization[]>(
      '/api/v1/users/without-organization',
      {},
      token
    );
  },

  /**
   * Update user notification preferences
   */
  async updateNotificationPreferences(
    userId: string,
    preferences: NotificationPreferences
  ): Promise<User> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<User>(
      `/api/v1/users/${userId}/notification-preferences`,
      {
        method: 'PATCH',
        body: JSON.stringify(preferences),
      },
      token
    );
  },
};
